import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import SEO from '../../../components/common/SEO';
import { toast } from 'react-toastify';
import { Modal, Button, Form, Badge } from 'react-bootstrap';
import ContentLoader from '../../../components/common/ContentLoader';

import { formatDateTime } from '../../../utils/dateUtils';
import { formatPrice } from '../../../utils/priceUtils';
import { generateHolidaySlug, generateHotelSlug, generateYatraSlug, generateMedicalSlug } from '../../../utils/slugUtils';
const MODULE_CONFIG = {
    'holidays': { backendKey: 'holiday-package', label: 'Holiday Package' },
    'hotels': { backendKey: 'hotel', label: 'Hotel' },
    'cabs': { backendKey: 'cab', label: 'Cab' },
    'doctors': { backendKey: 'doctor', label: 'Doctor' },
    'hospitals': { backendKey: 'hospital', label: 'Hospital' },
    'yatras': { backendKey: 'yatra', label: 'Yatra' },
};

const ASSET_BASE_PATH = process.env.REACT_APP_ASSET_BASE_PATH || 'http://localhost:5000';

// Fields that contain HTML content and should be rendered as HTML
const HTML_FIELDS = [
    'description', 'package_overview', 'hotel_overview', 'booking_policy',
    'places_to_visit', 'how_to_reach', 'bio', 'services', 'cab_description',
    'yatra_overview'
];

// Fields that are internal/system fields and should be hidden from display
const HIDDEN_FIELDS = [
    'Vendor', '_generic_title', '_generic_images', '_resolved_fields',
    'approved_by', 'approved_at', 'rejected_by', 'rejected_at',
    'Images', 'doctor_images', 'hospital_images', 'sub_speciality'
];

// Fields where raw IDs are resolved by the backend
const RESOLVED_FK_FIELDS = ['speciality_id', 'specialist_ids', 'hotel_ids', 'doctor_ids'];

const ListingApprovalDetail = () => {
    const { module, id } = useParams();
    const navigate = useNavigate();

    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);

    // Approve Modal State
    const [showApproveModal, setShowApproveModal] = useState(false);
    // Reject Modal State
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    const currentConfig = MODULE_CONFIG[module] || MODULE_CONFIG['holidays'];
    const backendModule = currentConfig.backendKey;

    const getPreviewUrl = () => {
        if (!listing) return '#';
        const params = '?preview=true';
        switch (module) {
            case 'holidays':
                return `/holiday-details/${generateHolidaySlug(listing)}${params}`;
            case 'hotels':
                return `/hotel-details/${generateHotelSlug(listing)}${params}`;
            case 'yatras':
                return `/yatra-details/${generateYatraSlug(listing)}${params}`;
            case 'doctors':
                return `/medical-details/doctor/${generateMedicalSlug(listing)}${params}`;
            case 'hospitals':
                return `/medical-details/hospital/${generateMedicalSlug(listing)}${params}`;
            case 'cabs':
                return `/cabs/search?preview_id=${listing.id}&preview=true`;
            default:
                return '#';
        }
    };

    const fetchListingDetail = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await superAdminApiClient.get(`/superadmin/listings/${backendModule}/${id}`);
            if (response.data.success) {
                setListing(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching listing details', error);
            toast.error('Failed to load listing details');
            navigate(`/superadmin/listings-approval/${module}`);
        } finally {
            setLoading(false);
        }
    }, [backendModule, id, module, navigate]);

    useEffect(() => {
        if (!MODULE_CONFIG[module]) {
            navigate('/superadmin/dashboard');
            return;
        }
        fetchListingDetail();
    }, [module, id, navigate, fetchListingDetail]);

    const handleApprove = async () => {
        try {
            const response = await superAdminApiClient.post(`/superadmin/listings/${backendModule}/${id}/approve`);
            if (response.data.success) {
                toast.success('Listing approved successfully');
                setShowApproveModal(false);
                fetchListingDetail();
            }
        } catch (error) {
            console.error('Error approving listing', error);
            toast.error(error.response?.data?.message || 'Failed to approve listing');
        }
    };

    const handleReject = async (e) => {
        e.preventDefault();
        if (!rejectionReason.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        try {
            const response = await superAdminApiClient.post(`/superadmin/listings/${backendModule}/${id}/reject`, {
                rejection_reason: rejectionReason
            });
            if (response.data.success) {
                toast.success('Listing rejected successfully');
                setShowRejectModal(false);
                fetchListingDetail();
            }
        } catch (error) {
            console.error('Error rejecting listing', error);
            toast.error(error.response?.data?.message || 'Failed to reject listing');
        }
    };

    const renderStatusBadge = (status) => {
        let variant = 'secondary';
        if (status === 'Published') variant = 'success';
        else if (status === 'Under Review') variant = 'warning';
        else if (status === 'Rejected') variant = 'danger';

        return <Badge bg={variant}>{status || 'Published'}</Badge>;
    };

    // Helper to format field names nicely: "hotel_ids" → "Hotels", "speciality_id" → "Speciality"
    const formatLabel = (key) => {
        if (key === 'id') return 'ID';
        // Make FK-resolved fields display nicer labels
        const labelOverrides = {
            'hotel_ids': 'Hotels',
            'doctor_ids': 'Doctors',
            'speciality_id': 'Speciality',
            'specialist_ids': 'Specialities',
        };
        if (labelOverrides[key]) return labelOverrides[key];
        return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    /**
     * Renders a cell value with smart formatting:
     * - HTML fields render as HTML
     * - FK ID fields use resolved names from backend
     * - JSON objects/arrays render in a readable format
     * - Booleans render as Yes/No
     * - Null/empty renders as N/A
     */
    const renderCellValue = (key, value) => {
        const resolvedFields = listing?._resolved_fields || {};

        // If this field has a backend-resolved value, use it
        if (RESOLVED_FK_FIELDS.includes(key) && resolvedFields[key]) {
            return <span>{resolvedFields[key]}</span>;
        }

        // Null / empty
        if (value === null || value === undefined || value === '') {
            return <span className="text-muted">N/A</span>;
        }

        // Boolean
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        // HTML fields: render as rendered HTML
        if (HTML_FIELDS.includes(key) && typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value)) {
            return (
                <div
                    className="html-content-preview"
                    style={{ maxHeight: '300px', overflowY: 'auto', padding: '8px', background: '#fafafa', borderRadius: '4px' }}
                    dangerouslySetInnerHTML={{ __html: value }}
                />
            );
        }

        // JSON objects/arrays: render in a readable list format
        if (typeof value === 'object') {
            return renderJsonValue(value);
        }

        // String that looks like JSON (starts with [ or {)
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
                try {
                    const parsed = JSON.parse(trimmed);
                    return renderJsonValue(parsed);
                } catch (e) { /* fall through to plain string */ }
            }
        }

        // Date fields
        if (typeof value === 'string' && (key.endsWith('_at') || key.endsWith('_date'))) {
            try {
                const d = new Date(value);
                if (!isNaN(d.getTime())) {
                    return formatDateTime(d);
                }
            } catch (e) { /* fall through */ }
        }

        // Price / fee fields
        if ((key.includes('price') || key.includes('fees') || key === 'amount' || key === 'per_km_rate') && (typeof value === 'number' || (typeof value === 'string' && value !== '' && !isNaN(Number(value))))) {
            return `₹${formatPrice(value)}`;
        }

        return String(value);
    };

    /**
     * Renders a parsed JSON value (object or array) in a readable, non-raw format.
     */
    const renderJsonValue = (value) => {
        if (Array.isArray(value)) {
            if (value.length === 0) return <span className="text-muted">None</span>;

            // Array of simple values (strings, numbers)
            if (value.every(v => typeof v !== 'object' || v === null)) {
                return (
                    <ul className="list-unstyled mb-0">
                        {value.map((item, idx) => (
                            <li key={idx}>{item !== null && item !== undefined ? String(item) : 'N/A'}</li>
                        ))}
                    </ul>
                );
            }

            // Array of objects (e.g., itinerary items, availability days)
            return (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {value.map((item, idx) => (
                        <div key={idx} className="border rounded p-2 mb-2 bg-light">
                            {typeof item === 'object' && item !== null ? (
                                <table className="table table-sm table-borderless mb-0">
                                    <tbody>
                                        {Object.entries(item).map(([k, v]) => (
                                            <tr key={k}>
                                                <td className="fw-medium text-muted" style={{ width: '40%' }}>{formatLabel(k)}</td>
                                                <td>
                                                    {v === null || v === '' ? (
                                                        <span className="text-muted">N/A</span>
                                                    ) : typeof v === 'object' ? (
                                                        renderJsonValue(v)
                                                    ) : typeof v === 'string' && /<\/?[a-z][\s\S]*>/i.test(v) ? (
                                                        <div dangerouslySetInnerHTML={{ __html: v }} />
                                                    ) : (
                                                        String(v)
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <span>{String(item)}</span>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        // Plain object
        if (typeof value === 'object' && value !== null) {
            return (
                <table className="table table-sm table-borderless mb-0">
                    <tbody>
                        {Object.entries(value).map(([k, v]) => (
                            <tr key={k}>
                                <td className="fw-medium text-muted" style={{ width: '40%' }}>{formatLabel(k)}</td>
                                <td>
                                    {v === null || v === '' ? (
                                        <span className="text-muted">N/A</span>
                                    ) : typeof v === 'object' ? (
                                        renderJsonValue(v)
                                    ) : (
                                        String(v)
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        return String(value);
    };

    if (loading) {
        return (
            <div className="row">
                <SEO title="Listing Details | Super Admin" />
                <ContentLoader />
            </div>
        );
    }

    if (!listing) return null;

    // Build the list of fields to display, excluding hidden/complex ones
    const displayFields = Object.keys(listing).filter(key => {
        if (HIDDEN_FIELDS.includes(key)) return false;
        return true;
    });

    return (
        <div className="row">
            <SEO title={`${currentConfig.label} Detail | Super Admin`} />
            
            <div className="col-lg-12">
                <div className="d-flex align-items-center mb-3">
                    <Link to={`/superadmin/listings-approval/${module}`} className="btn btn-sm btn-light me-3">
                        <i className="ri-arrow-left-line"></i> Back
                    </Link>
                    <h4 className="card-title mb-0 flex-grow-1">
                        Review {currentConfig.label}: {listing._generic_title}
                    </h4>
                    <div className="d-flex flex-wrap gap-2">
                        <a
                            href={getPreviewUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary"
                            title="View live preview of this listing"
                        >
                            <i className="ri-eye-line me-1"></i> View Page
                        </a>
                        {listing.status !== 'Published' && (
                            <button className="btn btn-success" onClick={() => setShowApproveModal(true)}>
                                <i className="ri-check-line me-1"></i> Approve
                            </button>
                        )}
                        {listing.status !== 'Rejected' && (
                            <button className="btn btn-danger" onClick={() => {
                                setRejectionReason('');
                                setShowRejectModal(true);
                            }}>
                                <i className="ri-close-line me-1"></i> Reject
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="col-lg-8">
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title mb-0">Listing Details</h5>
                    </div>
                    <div className="card-body">
                        <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                            <div className="flex-grow-1">
                                <h4 className="mb-1">{listing._generic_title}</h4>
                                <div className="text-muted">
                                    Current Status: <span className="ms-1">{renderStatusBadge(listing.status)}</span>
                                </div>
                            </div>
                        </div>

                        {listing.status === 'Rejected' && listing.rejection_reason && (
                            <div className="alert alert-danger mb-4">
                                <strong>Rejection Reason:</strong> {listing.rejection_reason}
                            </div>
                        )}

                        <div className="table-responsive">
                            <table className="table table-bordered table-striped mb-0">
                                <tbody>
                                    {displayFields.map(key => (
                                        <tr key={key}>
                                            <th className="bg-light" style={{ width: '30%', verticalAlign: 'top' }}>{formatLabel(key)}</th>
                                            <td style={{ wordBreak: 'break-word' }}>
                                                {renderCellValue(key, listing[key])}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {listing._generic_images && listing._generic_images.length > 0 && (
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Images</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                {listing._generic_images.map((img, idx) => (
                                    <div key={idx} className="col-md-4 col-sm-6">
                                        <div className="border rounded p-1">
                                            <img 
                                                src={img.image_url || (img.relative_path ? `${ASSET_BASE_PATH}/uploads/${img.relative_path}` : '/no_image_hotel.jpg')} 
                                                alt="Listing" 
                                                className="img-fluid rounded" 
                                                style={{ height: '150px', width: '100%', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '/no_image_hotel.jpg';
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="col-lg-4">
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title mb-0">Vendor Information</h5>
                    </div>
                    <div className="card-body">
                        {listing.Vendor ? (
                            <div className="d-flex flex-column gap-3">
                                <div>
                                    <h6 className="text-muted mb-1 fs-13">Vendor Name</h6>
                                    <p className="mb-0 fw-medium">{listing.Vendor.name}</p>
                                </div>
                                <div>
                                    <h6 className="text-muted mb-1 fs-13">Company Name</h6>
                                    <p className="mb-0 fw-medium">{listing.Vendor.company_name}</p>
                                </div>
                                <div>
                                    <h6 className="text-muted mb-1 fs-13">Email Address</h6>
                                    <p className="mb-0">{listing.Vendor.email}</p>
                                </div>
                                <div>
                                    <h6 className="text-muted mb-1 fs-13">Phone Number</h6>
                                    <p className="mb-0">{listing.Vendor.phone}</p>
                                </div>
                                <div className="mt-2">
                                    <Link to={`/superadmin/vendors/detail/${listing.Vendor.id}`} className="btn btn-sm btn-outline-primary w-100">
                                        View Full Vendor Profile
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <div className="text-muted text-center py-3">Vendor information not available</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Approve Confirmation Modal */}
            <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Approve Listing</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-1">Are you sure you want to <strong className="text-success">approve</strong> this listing?</p>
                    <p className="text-muted mb-0">
                        <strong>{listing._generic_title}</strong> will be published and visible to the public.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowApproveModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="success" onClick={handleApprove}>
                        <i className="ri-check-line me-1"></i> Yes, Approve
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reject Modal */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reject Listing</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleReject}>
                        <Form.Group className="mb-3">
                            <Form.Label>Reason for Rejection <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Enter the reason for rejection (this will be sent to the vendor)..."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="light" onClick={() => setShowRejectModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="danger" type="submit">
                                Confirm Rejection
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ListingApprovalDetail;
